import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Loader2, Trash2 } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useActivityLogger } from '../hooks/useActivityLogger';
import freightAIService from '../services/freightAIService';
import ApiManager from '../services/api/ApiManager';

const FreightAI = () => {
  const { user, userRole } = useUser();
  const { isDarkMode } = useTheme();
  const { logFreightAI } = useActivityLogger();
  
  // State
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Freight AI, your logistics assistant. I can help you search for loads, analyze lanes, provide rate insights, and calculate pricing. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [functionResult, setFunctionResult] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Handle sending message
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);
    
    try {
      // Prepare chat history for backend
      const chatHistory = messages.map(msg => ({
        role: msg.role === 'function' ? 'assistant' : msg.role,
        content: msg.content
      }));
      
      // Get AI response from backend
      const response = await freightAIService.sendMessage(userMessage, chatHistory);
      
      if (response.success) {
        // Add AI response to messages
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.message 
        }]);
        
        // If there are tool calls, format and display them
        if (response.hasToolCalls && response.toolCalls) {
          const formattedResults = freightAIService.formatToolResults(response.toolCalls);
          if (formattedResults) {
            setFunctionResult(formattedResults);
          }
        }
      } else {
        // Error response
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.message 
        }]);
      }
      
      // Log freight AI activity using centralized logger
      try {
        await logFreightAI({
          user_message: userMessage
        });
      } catch (logError) {
        console.warn('Failed to log freight AI activity:', logError);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear chat history
  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared. How can I help you today?"
    }]);
    setFunctionResult(null);
  };
  
  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  // Rate limit warning for Basic users
  const RateLimitWarning = () => {
    if (userRole !== 'Basic') return null;
    
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Basic plan: Limited to 10 AI requests per day. Upgrade for unlimited access.
        </p>
      </div>
    );
  };
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center gap-2">
          <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          Freight AI Assistant
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Your intelligent logistics companion
        </p>
        <div className="mt-4 flex justify-center">
        </div>
      </div>
      
      <RateLimitWarning />
      
      {/* Chat Container */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        {/* Messages Area */}
        <div className="h-[500px] overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <MessageBubble key={index} message={message} isDarkMode={isDarkMode} />
          ))}
          
          {isLoading && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Freight AI is thinking...</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input Area */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about loads, lanes, rates, or pricing..."
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       placeholder-gray-500 dark:placeholder-gray-400
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                       flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              Send
            </button>
            <button
              onClick={clearChat}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 
                       rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors
                       flex items-center gap-2"
            >
              <Trash2 className="w-5 h-5" />
              Clear
            </button>
          </div>
          
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Ask about load availability, lane analysis, rate insights, or pricing calculations
          </div>
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isDarkMode }) => {
  const isUser = message.role === 'user';
  const isFunction = message.role === 'function';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div className="flex items-start gap-2 mb-1">
          {!isUser && (
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          )}
          <div className={`px-4 py-2 rounded-lg ${
            isUser 
              ? 'bg-blue-600 text-white' 
              : isFunction
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
          }`}>
            <p className="whitespace-pre-wrap">{message.content}</p>
            
            {/* Display function data if available */}
            {message.data && (
              <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                <pre className="text-xs overflow-x-auto">
                  {JSON.stringify(message.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
          {isUser && (
            <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreightAI;